<template>
  <div class="min-h-screen bg-base-50">
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center h-96">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="container mx-auto py-8">
      <div class="alert alert-error">
        <Icon name="heroicons:exclamation-triangle" />
        <span>{{ error }}</span>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else-if="trip" class="max-w-6xl mx-auto py-8 px-4">
      <!-- Hero Section with Image -->
      <div class="relative mb-8">
        <!-- Main Image -->
        <div
          class="h-96 lg:h-[500px] relative overflow-hidden rounded-2xl shadow-xl"
        >
          <img
            v-if="trip.featured_image"
            :src="`/api/images/${trip.featured_image}`"
            :alt="trip.name"
            class="w-full h-full object-cover"
          />
          <div
            v-else
            class="w-full h-full bg-base-300 flex items-center justify-center"
          >
            <Icon
              name="heroicons:photo"
              class="text-6xl text-base-content/30"
            />
          </div>

          <!-- Overlay with trip info -->
          <div
            class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8 text-white"
          >
            <h1 class="text-4xl lg:text-5xl font-bold mb-4">{{ trip.name }}</h1>
            <div class="flex flex-wrap items-center gap-4 mb-4">
              <div class="flex items-center gap-2">
                <Icon name="heroicons:map-pin" class="w-5 h-5" />
                <span class="text-lg">
                  {{ trip.destination_start?.name }} →
                  {{ trip.destination_end?.name }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <Icon name="heroicons:calendar" class="w-5 h-5" />
                <span>{{ formatDateRange() }}</span>
              </div>
            </div>
            <div class="flex items-center gap-4">
              <div class="text-3xl font-bold text-base-100">
                {{ formatPrice(trip.price) }}€
              </div>
              <div v-if="trip.customizable" class="badge badge-accent badge-lg">
                Personalizable
              </div>
            </div>
          </div>
        </div>

        <!-- Image Gallery (if additional images exist) -->
        <div v-if="trip.images && trip.images.length > 0" class="mt-6">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              v-for="imageId in trip.images.slice(0, 6)"
              :key="imageId"
              class="aspect-video rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity shadow-md"
              @click="openImageModal(imageId)"
            >
              <img
                :src="`/api/images/${imageId}`"
                :alt="`${trip.name} - Imagen`"
                class="w-full h-full object-cover"
              />
            </div>
            <div
              v-if="trip.images.length > 6"
              class="aspect-video rounded-lg bg-base-300 flex items-center justify-center cursor-pointer hover:bg-base-400 transition-colors shadow-md"
              @click="openImageModal(trip.images[6])"
            >
              <div class="text-center">
                <Icon name="heroicons:plus" class="text-2xl mb-1" />
                <span class="text-sm">+{{ trip.images.length - 6 }} más</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-center mb-8">
        <div class="flex gap-4">
          <NuxtLink
            v-if="trip.customizable"
            :to="`/trips/customize/form?trip=${trip.id}`"
            class="btn btn-primary btn-lg"
          >
            <Icon name="heroicons:wrench-screwdriver" class="mr-2" />
            Personalizar Viaje
          </NuxtLink>
          <NuxtLink
            :to="`/trips/customize/simple?trip=${trip.id}`"
            class="btn btn-accent btn-lg"
          >
            <Icon name="heroicons:calendar" class="mr-2" />
            Reservar
          </NuxtLink>
        </div>
      </div>

      <!-- Route Map -->
      <div class="mb-8">
        <div class="card bg-base-100 shadow-lg">
          <div class="card-body">
            <h3 class="card-title mb-4">
              <Icon name="heroicons:map" class="text-primary" />
              Ruta del viaje
            </h3>

            <!-- Map with route or placeholder -->
            <div v-if="trip.calculated_route?.length" class="w-full">
              <TripRouteMap :route="trip.calculated_route" />
            </div>
            <div
              v-else
              class="w-full h-96 rounded-lg bg-base-200 flex items-center justify-center"
            >
              <div class="text-center">
                <Icon
                  name="heroicons:map"
                  class="w-16 h-16 mx-auto text-base-content/30 mb-4"
                />
                <h4 class="text-lg font-semibold mb-2">Ruta en preparación</h4>
                <p class="text-base-content/60">
                  Estamos calculando la mejor ruta para este viaje.<br />
                  La información del mapa estará disponible pronto.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trip Info Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <!-- Basic Info -->
        <div class="card bg-base-100 shadow-lg">
          <div class="card-body">
            <h3 class="card-title text-lg">
              <Icon name="heroicons:information-circle" class="text-primary" />
              Información Básica
            </h3>
            <div class="space-y-3">
              <div class="flex items-center gap-2">
                <Icon
                  name="heroicons:calendar"
                  class="w-4 text-base-content/60"
                />
                <span class="text-sm">{{ formatDateRange() }}</span>
              </div>
              <div class="flex items-center gap-2">
                <Icon
                  name="heroicons:map-pin"
                  class="w-4 text-base-content/60"
                />
                <span class="text-sm">
                  {{ trip.destination_start?.name }} →
                  {{ trip.destination_end?.name }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <Icon name="heroicons:users" class="w-4 text-base-content/60" />
                <span class="text-sm"
                  >{{ trip.segments?.length || 0 }} días</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Pricing -->
        <div class="card bg-base-100 shadow-lg">
          <div class="card-body">
            <h3 class="card-title text-lg">
              <Icon name="heroicons:currency-euro" class="text-success" />
              Precio
            </h3>
            <div class="space-y-2">
              <div class="text-2xl font-bold text-success">
                {{ formatPrice(trip.price) }}€
              </div>
              <div class="text-sm text-base-content/60">
                Precio base por persona
              </div>
              <div v-if="trip.customizable" class="badge badge-sm badge-info">
                Personalizable
              </div>
            </div>
          </div>
        </div>

        <!-- Transport -->
        <div class="card bg-base-100 shadow-lg">
          <div class="card-body">
            <h3 class="card-title text-lg">
              <Icon name="heroicons:truck" class="text-info" />
              Transporte
            </h3>
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <Icon
                  :name="getTransportIcon(trip.transport?.name)"
                  class="w-5 h-5 text-base-content/60"
                />
                <span class="text-sm">{{
                  trip.transport?.name || "No especificado"
                }}</span>
              </div>
              <div
                v-if="trip.transport?.description"
                class="text-sm text-base-content/60"
              >
                {{ trip.transport.description }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div v-if="trip.description" class="card bg-base-100 shadow-lg mb-8">
        <div class="card-body">
          <h3 class="card-title text-2xl mb-4">
            <Icon name="heroicons:document-text" class="text-primary" />
            Descripción del Viaje
          </h3>
          <p class="text-base-content/80 leading-relaxed text-lg">
            {{ trip.description }}
          </p>
        </div>
      </div>

      <!-- Categories -->
      <div
        v-if="trip.categories && trip.categories.length > 0"
        class="card bg-base-100 shadow-lg mb-8"
      >
        <div class="card-body">
          <h3 class="card-title text-2xl mb-4">
            <Icon name="heroicons:tag" class="text-primary" />
            Categorías
          </h3>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="category in trip.categories"
              :key="category.id"
              class="badge badge-outline badge-lg"
              :style="category.color ? `--badge-color: ${category.color}` : ''"
            >
              {{ category.name }}
            </div>
          </div>
        </div>
      </div>

      <!-- Segments/Itinerary -->
      <div v-if="trip.segments?.length" class="card bg-base-100 shadow-lg">
        <div class="card-body">
          <h3 class="card-title text-2xl mb-6">
            <Icon name="heroicons:map" class="text-primary" />
            Itinerario ({{ trip.segments.length }} días)
          </h3>

          <div class="space-y-6">
            <div
              v-for="(segment, index) in trip.segments"
              :key="segment.id"
              class="border border-base-300 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <!-- Segment Header -->
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-3">
                  <div class="badge badge-primary badge-lg">
                    Día {{ index + 1 }}
                  </div>
                  <h4 class="font-semibold text-lg">
                    {{ segment.destination_start?.name }} →
                    {{ segment.destination_end?.name }}
                  </h4>
                </div>
                <div v-if="segment.duration > 1" class="badge badge-outline">
                  {{ segment.duration }} días
                </div>
              </div>

              <!-- Segment Details -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Accommodation -->
                <div
                  v-if="segment.accommodation"
                  class="flex items-start gap-3 relative"
                >
                  <Icon
                    name="heroicons:building-office"
                    class="w-5 h-5 text-primary mt-1 absolute top-2 left-0"
                  />
                  <div class="ml-9">
                    <div class="font-medium">
                      <NuxtLink
                        :to="`/trips/accommodation/${segment.accommodation.id}`"
                        class="link link-primary hover:link-hover"
                      >
                        {{ segment.accommodation.name }}
                      </NuxtLink>
                    </div>
                    <div
                      v-if="segment.accommodation_room"
                      class="text-sm text-base-content/60"
                    >
                      <NuxtLink
                        :to="`/trips/accommodation/room/${segment.accommodation_room.id}`"
                        class="link link-primary hover:link-hover"
                      >
                        {{ segment.accommodation_room.name }}
                      </NuxtLink>
                    </div>
                    <div
                      v-if="segment.accommodation.description"
                      class="text-sm text-base-content/70 mt-1"
                    >
                      {{ segment.accommodation.description }}
                    </div>
                  </div>
                </div>

                <!-- Activities -->
                <div
                  v-if="segment.activities?.length"
                  class="flex items-start gap-3"
                >
                  <Icon
                    name="heroicons:play"
                    class="w-5 h-5 text-accent mt-1"
                  />
                  <div>
                    <div class="font-medium mb-1">Actividades</div>
                    <ul class="text-sm text-base-content/70">
                      <li
                        v-for="activity in segment.activities"
                        :key="activity.id || activity"
                        class="mb-1"
                      >
                        •
                        <NuxtLink
                          v-if="getActivityId(activity)"
                          :to="`/trips/activity/${getActivityId(activity)}`"
                          class="link link-primary hover:link-hover"
                        >
                          {{ getActivityName(activity) }}
                        </NuxtLink>
                        <span v-else>
                          {{ getActivityName(activity) }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Segment Description -->
              <div
                v-if="segment.description"
                class="mt-6 p-3 bg-base-200 rounded-lg"
              >
                <p class="text-sm">{{ segment.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Segments Message -->
      <div v-else class="card bg-base-100 shadow-lg">
        <div class="card-body text-center">
          <Icon
            name="heroicons:map"
            class="w-16 h-16 mx-auto text-base-content/30 mb-4"
          />
          <h3 class="text-xl font-bold mb-2">Itinerario Flexible</h3>
          <p class="text-base-content/60 mb-4">
            Este viaje no tiene un itinerario fijo. ¡Perfecto para personalizar
            según tus preferencias!
          </p>
          <NuxtLink
            v-if="trip.customizable"
            :to="`/trips/customize/form?trip=${trip.id}`"
            class="btn btn-primary"
          >
            <Icon name="heroicons:wrench-screwdriver" class="mr-2" />
            Crear tu Itinerario
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="selectedImage" class="modal modal-open">
      <div class="modal-box max-w-4xl">
        <button
          class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
          @click="selectedImage = null"
        >
          ✕
        </button>
        <img
          :src="`/api/images/${selectedImage}`"
          :alt="trip.name"
          class="w-full h-auto rounded-lg"
        />
      </div>
      <div class="modal-backdrop" @click="selectedImage = null"></div>
    </div>
  </div>
</template>

<script setup>
const route = useRoute();

const trip = ref(null);
const loading = ref(true);
const error = ref("");
const selectedImage = ref(null);

// Methods
async function fetchTrip() {
  try {
    loading.value = true;
    error.value = "";

    const response = await $fetch(`/api/trips/${route.params.id}`);
    trip.value = response;
  } catch (err) {
    console.error("Error fetching trip:", err);
    if (err.statusCode === 404) {
      error.value = "Viaje no encontrado";
    } else {
      error.value = "Error al cargar el viaje";
    }
  } finally {
    loading.value = false;
  }
}

function formatPrice(price) {
  if (!price) return "0";
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
}

function formatDateRange() {
  if (!trip.value) return "";

  const { start_day, start_month, end_day, end_month } = trip.value;

  if (!start_day || !start_month || !end_day || !end_month) {
    return "Fechas flexibles";
  }

  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  if (start_month === end_month) {
    return `${start_day} - ${end_day} ${months[start_month - 1]}`;
  } else {
    return `${start_day} ${months[start_month - 1]} - ${end_day} ${
      months[end_month - 1]
    }`;
  }
}

function getTransportIcon(transportName) {
  if (!transportName) return "heroicons:question-mark-circle";

  const name = transportName.toLowerCase();
  if (name.includes("autobús") || name.includes("bus"))
    return "heroicons:truck";
  if (name.includes("avión") || name.includes("avion"))
    return "heroicons:paper-airplane";
  if (name.includes("tren")) return "heroicons:rectangle-stack";
  if (name.includes("coche") || name.includes("car")) return "heroicons:truck";
  return "heroicons:map-pin";
}

function getActivityName(activity) {
  // If activity is an object with name property
  if (typeof activity === "object" && activity && activity.name) {
    return activity.name;
  }
  // If activity is just a string
  if (typeof activity === "string") {
    return activity;
  }
  // Fallback - return string representation
  return String(activity);
}

function getActivityId(activity) {
  // If activity is an object with id property
  if (typeof activity === "object" && activity && activity.id) {
    return activity.id;
  }
  // If activity is just a number (the ID itself)
  if (typeof activity === "number") {
    return activity;
  }
  // If activity is a string that represents a number
  if (typeof activity === "string" && !isNaN(parseInt(activity))) {
    return parseInt(activity);
  }
  return null;
}

function openImageModal(imageId) {
  selectedImage.value = imageId;
}

// Lifecycle
onMounted(fetchTrip);

// SEO
useHead({
  title: computed(() =>
    trip.value ? `${trip.value.name} - Soleando` : "Viaje - Soleando"
  ),
  meta: [
    {
      name: "description",
      content: computed(
        () => trip.value?.description || "Descubre este increíble viaje"
      ),
    },
  ],
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
